/* eslint-disable no-nested-ternary */
import { LoadingOutlined } from '@ant-design/icons';
import { Flex, message } from 'antd';
import { FormProvider } from 'antd/es/form/context';
import React, {
  useMemo, useState, useEffect
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { Typography, Image } from 'tfc-components';

import QuestionPage from './QuestionPage';

import Button from 'components/atoms/Button';
import Loading from 'components/atoms/Loading';
import useWindowDimensions from 'hooks/useWindowDemensions';
import { getAllOnboardingService, getOnboardingByIdService, saveOnboardingService } from 'services/onboarding';
import { useAppSelector } from 'store/hooks';
import { ROUTE_PATHS } from 'utils/constants';

const Onboarding: React.FC = () => {
  const profile = useAppSelector((state) => state.auth.profile);
  const navigate = useNavigate();
  const { height } = useWindowDimensions();
  const method = useForm<{ [code: string]: string[] }>();
  /* States */
  const [agent, setAgent] = useState<number>();
  const [questionIndex, setQuestionIndex] = useState(0);

  const { data: onboarding, isLoading: mainLoading } = useQuery({
    queryKey: ['onboarding'],
    queryFn: getAllOnboardingService
  });

  const { data: onboardingDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['onboarding-detail', agent],
    queryFn: () => getOnboardingByIdService(agent!),
    enabled: !!agent
  });

  const questions = useMemo(() => onboardingDetail?.pages, [onboardingDetail]);

  const formData = useWatch({ control: method.control });
  const flatAnswers = useMemo(() => Object.values(formData).flat() as string[], [formData]);

  const isNextDisabled = useMemo(() => {
    if (!questions || !questions[questionIndex]) return true;

    return questions[questionIndex].questions.some((q) => {
      const isVisible = q.condition && q.condition.requireAnyOption
        ? q.condition.requireAnyOption.some((rq) => flatAnswers.includes(rq))
        : true;

      if (!isVisible) return false;

      const value = formData[q.code];
      return !value || value.length === 0;
    });
  }, [formData, questions, questionIndex, flatAnswers]);

  const { mutate: submitOnboarding, isLoading: isSubmitting } = useMutation(
    ['submitOnboarding'],
    saveOnboardingService,
    {
      onSuccess: () => {
        navigate(ROUTE_PATHS.AGENT);
      },
      onError: () => {
        message.error('Something went wrong, please try again later');
      }
    }
  );

  // Logic reset toàn cục khi dependency thay đổi
  useEffect(() => {
    if (!onboardingDetail?.pages) return;

    let hasChanged = false;
    const currentFormValues = method.getValues();
    const newValues = { ...currentFormValues };

    onboardingDetail.pages.forEach((page) => {
      page.questions.forEach((q) => {
        const questionValue = currentFormValues[q.code] || [];
        if (questionValue.length === 0) return;

        // 1. Kiểm tra condition của Question
        const isQuestionVisible = q.condition && q.condition.requireAnyOption
          ? q.condition.requireAnyOption.some((rq) => flatAnswers.includes(rq))
          : true;

        if (!isQuestionVisible) {
          newValues[q.code] = [];
          hasChanged = true;
          return;
        }

        // 2. Kiểm tra condition của từng Option đang được chọn
        const filteredOptions = questionValue.filter((selectedOptionCode) => {
          const option = q.options.find((opt) => opt.code === selectedOptionCode);
          if (!option || !option.condition?.requireAnyOption?.length) return true;
          return option.condition.requireAnyOption.some((rq) => flatAnswers.includes(rq));
        });

        if (filteredOptions.length !== questionValue.length) {
          newValues[q.code] = filteredOptions;
          hasChanged = true;
        }
      });
    });

    if (hasChanged) {
      Object.entries(newValues).forEach(([code, val]) => {
        if (JSON.stringify(val) !== JSON.stringify(currentFormValues[code])) {
          method.setValue(code, val);
        }
      });
    }
  }, [flatAnswers, onboardingDetail, method]);
  if (!profile) {
    return <Navigate to={ROUTE_PATHS.LOGIN} state={{ path: ROUTE_PATHS.ONBOARDING }} />;
  }

  return (
    <div className="p-onboarding">
      {!agent ? (
        <Flex justify="center" align="center" vertical gap={12} className="p-onboarding_getstarted" style={{ minHeight: `${height}px` }}>
          {mainLoading ? <LoadingOutlined style={{ color: '#ff6b00', fontSize: 18 }} /> : (
            <>
              <Typography.Text extendClasses="fs-24x32 color-white" fontweight="700" textStyle="center">
                Let&apos;s start with your agent
              </Typography.Text>
              <Typography.Text extendClasses="fs-14x22 color-white" fontweight="400" textStyle="center">
                Pick the agent type that best fits your business needs.
              </Typography.Text>
              <div className="p-onboarding_agent">
                {onboarding?.map((item) => (
                  <Flex vertical gap={12} className="p-onboarding_agent_item" key={item.id}>
                    <Image imgSrc={item.images?.[0] || ''} alt={item.title} ratio={283 / 160} />
                    <Typography.Text extendClasses="fs-16x24 color-eerie-black" fontweight="700">
                      {item.title}
                    </Typography.Text>
                    <Typography.Text extendClasses="fs-14x22 color-davys-grey" fontweight="400">
                      {item.description}
                    </Typography.Text>
                    <Flex justify="end" className="p-onboarding_agent_footer">
                      <Button onClick={() => setAgent(item.id)}>Get started</Button>
                    </Flex>
                  </Flex>
                ))}
              </div>
            </>
          )}
        </Flex>
      ) : detailLoading ? <Loading variant="fullscreen" /> : (
        <FormProvider {...method}>
          <Flex className="p-onboarding_question" style={{ height: `${height}px` }}>
            {questions && (
              <Flex vertical className="p-onboarding_question_content">
                <div className="p-onboarding_question_body">
                  {questions[questionIndex].questions.reduce((acc, q) => {
                    const { condition } = q;
                    const isVisible = condition && condition.requireAnyOption
                      ? condition.requireAnyOption.some((rq) => flatAnswers.includes(rq))
                      : true;

                    if (!isVisible) return acc;

                    // Only show this question if the previous visible one is answered.
                    const lastQuestion = acc[acc.length - 1];
                    if (lastQuestion) {
                      const lastQCode = lastQuestion.props.question.code;
                      const lastValue = formData[lastQCode];
                      const isLastAnswered = lastValue && lastValue.length > 0;
                      if (!isLastAnswered) return acc;
                    }

                    return [
                      ...acc,
                      <QuestionPage key={q.code} question={q} method={method} />
                    ];
                  }, [] as React.ReactElement[])}
                </div>
                <Flex justify="space-between" className="p-onboarding_question_footer">
                  {questionIndex > 0 && (
                    <Button variant="outline" onClick={() => setQuestionIndex((prev) => prev - 1)}>
                      Back
                    </Button>
                  )}
                  <Button
                    className="p-onboarding_question_next"
                    isLoading={isSubmitting}
                    disabled={isNextDisabled}
                    onClick={() => {
                      if (questionIndex < questions.length - 1) {
                        setQuestionIndex((prev) => prev + 1);
                      } else {
                        submitOnboarding({
                          onboardingModeId: agent!,
                          answers: Object.entries(method.getValues()).map(([code, value]) => ({
                            questionCode: code,
                            options: value
                          }))
                        });
                      }
                    }}
                  >
                    Next
                  </Button>
                </Flex>
              </Flex>
            )}
            <div className="p-onboarding_question_thumbnail">
              <Image imgSrc={questions?.[questionIndex]?.images?.[0] || ''} alt="Onboarding" ratio={683 / 768} />
            </div>
          </Flex>
        </FormProvider>
      )}
    </div>
  );
};

export default Onboarding;
